import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Info,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react'
import { apiService } from '../../services/api'
import { cn } from '../../utils/cn'
import { useAuth } from '../../context/AuthContext'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'

// ── Helpers ─────────────────────────────────────────────────────────────────

const timeAgo = (timestamp) => {
  const diff = Date.now() - new Date(timestamp).getTime()
  const mins = Math.floor(diff / 60000)
  const hrs  = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1)  return 'Just now'
  if (mins < 60) return `${mins}m ago`
  if (hrs < 24)  return `${hrs}h ago`
  if (days < 7)  return `${days}d ago`
  return new Date(timestamp).toLocaleDateString()
}

const extractId = (url = '') => {
  const match = url.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)
  return match ? match[1] : null
}

const typeConfig = {
  success: {
    icon: CheckCircle,
    iconClass: 'text-emerald-500',
    boxClass:  'bg-emerald-100 dark:bg-emerald-900/30',
    dotClass:  'bg-emerald-500',
    label: 'Success',
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-amber-500',
    boxClass:  'bg-amber-100 dark:bg-amber-900/30',
    dotClass:  'bg-amber-500',
    label: 'Warning',
  },
  error: {
    icon: AlertCircle,
    iconClass: 'text-red-500',
    boxClass:  'bg-red-100 dark:bg-red-900/30',
    dotClass:  'bg-red-500',
    label: 'Error',
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-500',
    boxClass:  'bg-blue-100 dark:bg-blue-900/30',
    dotClass:  'bg-blue-500',
    label: 'Info',
  },
}

const getTypeConfig = (type) => typeConfig[type] || typeConfig.info

// ── Main component ───────────────────────────────────────────────────────────

const NotificationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || 'all')
  const [typeFilter,   setTypeFilter]   = useState(searchParams.get('type')   || 'all')
  const queryClient = useQueryClient()
  const navigate    = useNavigate()
  const { user }    = useAuth()
  const role        = user?.role

  // ── Data ──────────────────────────────────────────────────────────────────

  const { data: notificationsData, isLoading } = useQuery(
    ['notifications-page', typeFilter],
    () => {
      const params = new URLSearchParams()
      if (typeFilter !== 'all') params.append('type', typeFilter)
      params.append('limit', '100')
      return apiService.get(`/notifications?${params.toString()}`).then(r => r.data)
    },
    { staleTime: 30000, retry: 1, refetchOnWindowFocus: false }
  )

  // ── Mutations ─────────────────────────────────────────────────────────────

  const invalidate = () => {
    queryClient.invalidateQueries('notifications-page')
    queryClient.invalidateQueries('notifications-unread-count')
    queryClient.invalidateQueries('notifications')
  }

  const markAsReadMutation = useMutation(
    (id) => apiService.patch(`/notifications/${id}/read`),
    { onSuccess: invalidate }
  )

  const markAllAsReadMutation = useMutation(
    () => apiService.patch('/notifications/mark-all-read'),
    { onSuccess: invalidate }
  )

  const deleteNotificationMutation = useMutation(
    (id) => apiService.delete(`/notifications/${id}`),
    { onSuccess: invalidate }
  )

  // ── Routing ───────────────────────────────────────────────────────────────

  const getLink = (notification) => {
    const data = notification.data
      ? (typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data)
      : {}
    const actionUrl = data.actionUrl || ''
    const title   = notification.title.toLowerCase()
    const message = notification.message.toLowerCase()

    if (
      title.includes('edit request') || message.includes('edit request') ||
      notification.type === 'edit_request' || actionUrl.includes('/requests')
    ) {
      const id = data.requestId || extractId(actionUrl)
      if (role === 'admin') return id ? `/admin/requests?open=${id}` : '/admin/requests'
      return id ? `/client/requests?open=${id}` : '/client/requests'
    }

    if (
      title.includes('support') || message.includes('support') ||
      notification.type === 'support' || notification.type === 'info' ||
      actionUrl.includes('/support')
    ) {
      const id = data.ticketId || extractId(actionUrl)
      if (role === 'admin') return id ? `/admin/support?open=${id}` : '/admin/support'
      return id ? `/client/support?open=${id}` : '/client/support'
    }

    if (
      title.includes('site published') || message.includes('published') ||
      notification.type === 'site_published'
    ) {
      if (role === 'admin') return data.siteId ? `/admin/sites/${data.siteId}` : '/admin/sites'
      return data.siteId ? `/client/sites/${data.siteId}` : '/client/sites'
    }

    if (
      title.includes('payment') || message.includes('payment') ||
      title.includes('billing') || notification.type === 'billing'
    ) {
      if (role === 'admin') return data.clientId ? `/admin/billing?client=${data.clientId}` : '/admin/billing'
      return '/client/billing'
    }

    if (actionUrl && !actionUrl.includes('localhost') && !actionUrl.includes('/dashboard')) {
      return actionUrl
    }

    return role === 'admin' ? '/admin' : '/client'
  }

  const handleClick = (notification) => {
    if (!notification.is_read) markAsReadMutation.mutate(notification.id)
    const link = getLink(notification)
    if (link && link !== window.location.pathname) navigate(link)
  }

  // ── Derived state ─────────────────────────────────────────────────────────

  const allNotifications = notificationsData?.notifications || []

  const displayed = allNotifications.filter(n => {
    if (statusFilter === 'unread') return !n.is_read
    if (statusFilter === 'read')   return  n.is_read
    return true
  })

  const unreadCount = allNotifications.filter(n => !n.is_read).length
  const countFor    = (f) => f === 'all' ? allNotifications.length
    : f === 'unread' ? allNotifications.filter(n => !n.is_read).length
    : allNotifications.filter(n => n.is_read).length

  const updateStatusFilter = (f) => {
    setStatusFilter(f)
    setSearchParams({ filter: f, type: typeFilter })
  }

  const updateTypeFilter = (t) => {
    setTypeFilter(t)
    setSearchParams({ filter: statusFilter, type: t })
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 pt-4">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
              Notifications
            </h1>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center h-6 min-w-6 px-2 text-xs font-bold bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400 rounded-full">
                {unreadCount} unread
              </span>
            )}
          </div>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            All your notifications in one place.
          </p>
        </div>

        {unreadCount > 0 && (
          <Button
            variant="outline"
            onClick={() => markAllAsReadMutation.mutate()}
            disabled={markAllAsReadMutation.isLoading}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <CheckCheck className="w-4 h-4" />
            Mark all as read
          </Button>
        )}
      </div>

      {/* Filter toolbar */}
      <Card className="bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">

            {/* Status tabs */}
            <div className="flex items-center gap-1">
              {['all', 'unread', 'read'].map((f) => (
                <button
                  key={f}
                  onClick={() => updateStatusFilter(f)}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 capitalize',
                    statusFilter === f
                      ? 'bg-accent-600 text-white'
                      : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800/60 hover:text-neutral-900 dark:hover:text-neutral-200'
                  )}
                >
                  {f}
                  {countFor(f) > 0 && (
                    <span className={cn(
                      'ml-1.5 text-xs',
                      statusFilter === f ? 'text-white/80' : 'text-neutral-400 dark:text-neutral-500'
                    )}>
                      {countFor(f)}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-5 bg-neutral-200 dark:bg-neutral-700" />

            {/* Type filter */}
            <div className="flex items-center gap-1 flex-wrap">
              {['all', 'info', 'success', 'warning', 'error'].map((t) => {
                const cfg = t !== 'all' ? getTypeConfig(t) : null
                return (
                  <button
                    key={t}
                    onClick={() => updateTypeFilter(t)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 capitalize',
                      typeFilter === t
                        ? 'bg-neutral-100 dark:bg-neutral-700/60 text-neutral-900 dark:text-neutral-100'
                        : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800/60 hover:text-neutral-700 dark:hover:text-neutral-200'
                    )}
                  >
                    {cfg && (
                      <span className={cn('w-2 h-2 rounded-full flex-shrink-0', cfg.dotClass)} />
                    )}
                    {t}
                  </button>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification list */}
      <Card className="bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        {isLoading ? (
          <CardContent className="py-16 flex items-center justify-center gap-3 text-neutral-500 dark:text-neutral-400">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-600 border-t-transparent" />
            <span className="text-sm">Loading notifications…</span>
          </CardContent>
        ) : displayed.length === 0 ? (
          <CardContent className="py-16 text-center">
            <div className="w-14 h-14 mx-auto mb-4 bg-neutral-100 dark:bg-neutral-800/60 rounded-2xl flex items-center justify-center">
              <Bell className="w-7 h-7 text-neutral-400" />
            </div>
            <p className="text-base font-medium text-neutral-700 dark:text-neutral-300">
              {statusFilter === 'unread' ? "You're all caught up!" : 'No notifications'}
            </p>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">
              {statusFilter === 'unread'
                ? 'Nothing unread right now.'
                : 'Notifications will appear here when something happens.'}
            </p>
          </CardContent>
        ) : (
          <div>
            {displayed.map((notification, idx) => {
              const cfg = getTypeConfig(notification.type)
              const Icon = cfg.icon
              const isLast = idx === displayed.length - 1

              return (
                <div
                  key={notification.id}
                  onClick={() => handleClick(notification)}
                  className={cn(
                    'flex items-start gap-4 px-5 py-4 cursor-pointer transition-all duration-150 group',
                    !isLast && 'border-b border-neutral-100 dark:border-neutral-700/40',
                    !notification.is_read
                      ? 'border-l-[3px] border-l-accent-500 pl-[17px] hover:bg-accent-50/40 dark:hover:bg-accent-900/10'
                      : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/40'
                  )}
                >
                  {/* Type icon */}
                  <div className={cn(
                    'flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mt-0.5',
                    cfg.boxClass
                  )}>
                    <Icon className={cn('w-4 h-4', cfg.iconClass)} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className={cn(
                          'text-sm font-semibold truncate',
                          notification.is_read
                            ? 'text-neutral-700 dark:text-neutral-300'
                            : 'text-neutral-900 dark:text-neutral-50'
                        )}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <span className="flex-shrink-0 w-2 h-2 rounded-full bg-accent-500" />
                        )}
                      </div>

                      {/* Hover actions */}
                      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!notification.is_read && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markAsReadMutation.mutate(notification.id) }}
                            className="p-1.5 rounded-lg text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 hover:bg-neutral-100 dark:hover:bg-neutral-700/60 transition-colors"
                            title="Mark as read"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteNotificationMutation.mutate(notification.id) }}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-neutral-100 dark:hover:bg-neutral-700/60 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 leading-relaxed">
                      {notification.message}
                    </p>

                    <div className="flex items-center gap-1.5 mt-1.5 text-xs text-neutral-400 dark:text-neutral-500">
                      <Clock className="w-3 h-3" />
                      {timeAgo(notification.created_at)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}

export default NotificationsPage
